/**
 * O analisador do formato `text/event-stream` (H02, C21).
 *
 * ── Por que escrever isto, se o navegador tem `EventSource` ───────────────
 *
 * Porque o `EventSource` **não manda cabeçalho**. Nenhum. E o `/v1/eventos`
 * exige `Authorization: Bearer` como toda rota da API (B14).
 *
 * As saídas seriam: mandar o token na query — que o coloca no log de acesso do
 * servidor, no histórico do navegador e no `Referer`, tudo por escrito e para
 * sempre — ou trocar a API para aceitar cookie, o que abriria CSRF numa
 * superfície que hoje não tem. Ler o fluxo por `fetch` custa este arquivo e não
 * custa nenhuma das duas.
 *
 * O que se perde do `EventSource` é a reconexão automática e o envio do
 * `Last-Event-ID`. Os dois são reimplementados no `fluxo-sse.ts` — e a H02
 * exige tratamento próprio de reconexão de qualquer forma, então o que era
 * perda vira controle.
 *
 * ── Analisador separado do transporte ─────────────────────────────────────
 *
 * Este arquivo não conhece rede: recebe texto, devolve evento. É o que permite
 * provar as partes chatas do formato — evento partido no meio de um pedaço,
 * `data:` em várias linhas, comentário de batimento — sem servidor nenhum.
 */

export interface EventoSse {
  /** `id:` — vira `Last-Event-ID` na reconexão. */
  readonly id: string | null;
  /** `event:` — o servidor manda `mudou`. */
  readonly tipo: string;
  /** `data:`, com as linhas já reunidas. */
  readonly dados: string;
}

/**
 * Acumula pedaços e devolve os eventos completos.
 *
 * Guarda estado porque a rede não respeita fronteira de evento: um pedaço pode
 * terminar no meio de `data: {"recur`. Analisar pedaço a pedaço sem acumular
 * produziria JSON quebrado de vez em quando — o tipo de defeito que só aparece
 * sob carga.
 */
export class AnalisadorSse {
  private resto = '';

  alimentar(pedaco: string): EventoSse[] {
    // `\r\n` é permitido pelo formato e alguns proxies convertem. Normalizar na
    // entrada evita `\r` grudado no fim de todo campo.
    this.resto += pedaco.replace(/\r\n?/g, '\n');

    const eventos: EventoSse[] = [];
    let corte: number;

    // Linha em branco separa eventos. O último trecho fica no resto — ele pode
    // ser um evento incompleto, e despachá-lo entregaria dado pela metade.
    while ((corte = this.resto.indexOf('\n\n')) !== -1) {
      const bruto = this.resto.slice(0, corte);
      this.resto = this.resto.slice(corte + 2);

      const evento = interpretarBloco(bruto);
      if (evento) eventos.push(evento);
    }

    return eventos;
  }
}

function interpretarBloco(bruto: string): EventoSse | null {
  let id: string | null = null;
  let tipo = 'message';
  const dados: string[] = [];

  for (const linha of bruto.split('\n')) {
    // Comentário. O servidor manda `: batimento` a cada 25 s para o proxy não
    // fechar a conexão ociosa — e é só isso que ele é: sinal de vida, não
    // evento.
    if (linha.startsWith(':')) continue;

    const doisPontos = linha.indexOf(':');
    const campo = doisPontos === -1 ? linha : linha.slice(0, doisPontos);
    // O formato manda descartar UM espaço depois dos dois-pontos, e só um.
    // Cortar todos comeria a indentação de um `data:` com JSON formatado.
    const valorBruto = doisPontos === -1 ? '' : linha.slice(doisPontos + 1);
    const valor = valorBruto.startsWith(' ') ? valorBruto.slice(1) : valorBruto;

    if (campo === 'id') id = valor;
    else if (campo === 'event') tipo = valor;
    else if (campo === 'data') dados.push(valor);
    // `retry` é ignorado de propósito: quem decide o intervalo aqui é o
    // `fluxo-sse.ts`, com recuo progressivo. Aceitar o valor do servidor
    // deixaria o cliente sem defesa contra um `retry: 0` acidental.
  }

  // Bloco só de comentário não é evento.
  if (dados.length === 0 && id === null) return null;

  return { id, tipo, dados: dados.join('\n') };
}
